import { AppBar, IconButton, Toolbar, Box } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch, useLocation } from 'react-router-dom';
import { fetchUsers, fetchMe } from 'actions/action-lookups';
import Notification from 'containers/shared/notification/notification';
import { getTaskTypesInOrder } from 'services/utils/task-service';
import { ROUTES } from 'constants/index';
import { useTheme } from '@mui/styles';
import { useTypedSelector } from 'hooks/use-typed-selector';
import { createSelector } from '@reduxjs/toolkit';
import { TrellisLogo } from '../icons/icons';
import { styles } from './nav-styles';
import PatientsComponent from './patients';
import ApplicationManagerComponent from './application-manager/index';
import ReportsComponent from './reports';
import AccreditationComponent from './accreditation';
import ScheduleComponent from './schedule';
import ShippingComponent from './shipping/index';
import FinancialAssistanceFundsComponent from './financial-assistance-funds/index';
import TopNav from './top-nav';
import ToolsMenu from '../../tools/tools_menu';
import { getPatientsUrl, getCustomerIdFromLocation } from '../../helpers/router';
import { MenuStructure } from './application-manager/menu-structure';

const selectPatientsUrl = createSelector(
  [
    state => state.taskCounts.data,
    state => state.sidebarTaskType,
    state => state.tabControl.patientTab,
  ],
  (taskCounts, sidebarTaskType, patientTab) => {
    const taskTypes = getTaskTypesInOrder(taskCounts);
    const taskType =
      sidebarTaskType || taskTypes.find(type => taskCounts[type].task_cnt) || taskTypes[0];
    return getPatientsUrl(taskType, patientTab);
  },
);

const useNavStyles = () => {
  const theme = useTheme();
  return styles(theme);
};

const Nav = () => {
  const dispatch = useDispatch();
  const style = useNavStyles();
  const location = useLocation();
  const isUserGuide = useMemo(() => location.pathname.includes('/user-guide/'), [location]);
  const patientsUrl = useTypedSelector(selectPatientsUrl);
  const [showMenu, setShowMenu] = useState(null);

  const user = useSelector(state => state.auth?.currentUser);
  const canAccessReportsLink =
    user?.permissions?.access_reports?.customers.includes(getCustomerIdFromLocation()) || false;
  const canAccessAccreditationLink =
    user?.permissions?.access_accreditation?.customers.includes(getCustomerIdFromLocation()) ||
    false;
  const canAccessAppManager = user?.permissions?.access_app_manager || false;
  const canSeeFaFundsLink =
    user?.permissions?.access_fa_funds_search?.customers.includes(getCustomerIdFromLocation()) ||
    false;

  useEffect(() => {
    if (isUserGuide) return;
    dispatch(fetchUsers());
    dispatch(fetchMe());
  }, [dispatch, isUserGuide]);

  const rightClicked = async e => {
    const eas = localStorage.getItem('enable_arbor_shortcuts');
    if (eas && e.altKey) {
      e.preventDefault();
      setShowMenu({ x: e.clientX, y: e.clientY });
    }
  };

  return (
    !isUserGuide && (
      <Box sx={style.root} onContextMenu={rightClicked}>
        <Notification />
        <AppBar sx={style.appBar}>
          <Toolbar disableGutters sx={style.appBar}>
            <IconButton
              color="inherit"
              aria-label="Open drawer"
              sx={styles.menuButton}
              size="large"
            >
              <TrellisLogo to={patientsUrl} />
              {showMenu !== null ? (
                <ToolsMenu setShowMenu={setShowMenu} position={showMenu} />
              ) : null}
            </IconButton>
            <TopNav />
          </Toolbar>
        </AppBar>
        <Box sx={style.content} component="main">
          <Switch>
            {canAccessAppManager && (
              <Route
                path={ROUTES.APPLICATION_MANAGER}
                render={routeProps => (
                  <ApplicationManagerComponent menu={MenuStructure} {...routeProps} />
                )}
              />
            )}
            <Route path="/customers/:customerId/patients" component={PatientsComponent} />
            {canAccessAccreditationLink && (
              <Route
                path="/customers/:customerId/accreditation"
                component={AccreditationComponent}
              />
            )}
            <Route path="/customers/:customerId/schedule" component={ScheduleComponent} />
            {canAccessReportsLink && (
              <Route path="/customers/:customerId/reports" component={ReportsComponent} />
            )}
            <Route path="/customers/:customerId/shipping" component={ShippingComponent} />
            {canSeeFaFundsLink && (
              <Route
                path="/customers/:customerId/financial-assistance-funds"
                component={FinancialAssistanceFundsComponent}
              />
            )}
            <Route path="/customers/:customerId/" component={PatientsComponent} />
            <Route path="/" component={PatientsComponent} />
          </Switch>
        </Box>
      </Box>
    )
  );
};

export default Nav;
